import httpClient from '@/lib/httpClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface AppNotification {
  id: string
  data: {
    type: string
    title?: string
    message: string
    team_id?: number
    team_name?: string
    action_path?: string
  }
  read_at: string | null
  created_at: string
}

interface NotificationsPage {
  data: AppNotification[]
  unread_count: number
  has_more: boolean
}

const UNREAD_KEY = ['notifications', 'unread-count']
const LIST_KEY = ['notifications', 'list']

/** Unread count for the bell badge; polled so new alerts show without a reload. */
export function useUnreadNotificationCount() {
  return useQuery<number>({
    queryKey: UNREAD_KEY,
    queryFn: async () =>
      (await httpClient.get('notifications/unread-count')).data.unread_count,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
}

export function useNotifications({ enabled }: { enabled: boolean }) {
  return useQuery<NotificationsPage>({
    queryKey: LIST_KEY,
    queryFn: async () => (await httpClient.get('notifications')).data,
    enabled,
  })
}

function useNotificationMutation(
  request: (id?: string) => Promise<{ unread_count: number }>,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: request,
    onSuccess: ({ unread_count }) => {
      queryClient.setQueryData(UNREAD_KEY, unread_count)
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
    },
  })
}

export function useMarkNotificationRead() {
  return useNotificationMutation(
    async (id) => (await httpClient.post(`notifications/${id}/read`)).data,
  )
}

export function useMarkAllNotificationsRead() {
  return useNotificationMutation(
    async () => (await httpClient.post('notifications/read-all')).data,
  )
}
